import { useEffect, useMemo, useState } from 'react';
import { getStages } from './i18n/stages';
import { useLanguage } from './i18n/context';
import { getUi } from './i18n/ui';
import { useTheme } from './hooks/useTheme';
import { useUserData } from './hooks/useUserData';
import { useFamilyAutoSync } from './hooks/useFamilyAutoSync';
import { loadBackupFromCloud, saveBackupToCloud } from './lib/backupApi';
import { loadFamilySyncPrefs, type FamilySyncPrefs } from './lib/familySync';
import { applyCustomizationsToStages, customizationsSignature } from './lib/customizations';
import { loadReminderPrefs, maybeLocalDailyReminder } from './lib/reminders';
import type { UseChecklistResult } from './types';
import { Hero } from './components/Hero';
import { StageNav } from './components/StageNav';
import { StageView } from './components/StageView';
import { Disclaimer } from './components/Disclaimer';
import { LanguageSwitcher } from './components/LanguageSwitcher';
import { Onboarding } from './components/Onboarding';
import { ProfileSettings } from './components/ProfileSettings';
import { TodayFocus } from './components/TodayFocus';
import { WeekCard } from './components/WeekCard';
import { SearchBox } from './components/SearchBox';
import { SharePanel } from './components/SharePanel';
import { InstallAppCard } from './components/InstallAppCard';
import { ReminderCard } from './components/ReminderCard';
import styles from './App.module.css';

function App() {
  const { locale, setLocale } = useLanguage();
  const ui = getUi(locale);
  const { isDark, toggle } = useTheme();
  const user = useUserData(locale);
  const baseStages = useMemo(() => getStages(locale), [locale]);
  const stages = useMemo(
    () => applyCustomizationsToStages(baseStages, user.customizations),
    [baseStages, user.customizations]
  );
  const [activeStageId, setActiveStageId] = useState(stages[0].id);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [stageTouched, setStageTouched] = useState(false);
  const [familySyncPrefs, setFamilySyncPrefs] = useState<FamilySyncPrefs | null>(() =>
    loadFamilySyncPrefs()
  );

  const totalCount = useMemo(
    () => stages.reduce((sum, s) => sum + s.checklist.length, 0),
    [stages]
  );

  const checkedSignature = `${user.account?.checked?.slice().sort().join('|') ?? ''}::${customizationsSignature(user.customizations)}`;

  useFamilyAutoSync({
    enabled: Boolean(familySyncPrefs?.autoSync && familySyncPrefs.code && user.account),
    checkedSignature,
    exportBackup: user.exportActiveBackup,
    applyFamilyShare: user.applyFamilyShare,
  });

  // アクティブユーザーの言語に合わせる
  useEffect(() => {
    if (user.account?.locale && user.account.locale !== locale) {
      setLocale(user.account.locale);
    }
  }, [user.account?.id, user.account?.locale]);

  useEffect(() => {
    if (stageTouched) return;
    if (user.snapshot.mode === 'unset') return;
    setActiveStageId(user.snapshot.stageId);
  }, [user.snapshot.mode, user.snapshot.stageId, stageTouched]);

  const activeStage = useMemo(
    () => stages.find((s) => s.id === activeStageId) ?? stages[0],
    [activeStageId, stages]
  );

  const uncheckedHint = useMemo(() => {
    const remaining = activeStage.checklist.filter((item) => !user.isChecked(activeStage.id, item.id));
    if (remaining.length === 0) {
      return locale === 'en'
        ? 'Nice work — checklist looks clear for this stage.'
        : locale === 'zh'
          ? '这一阶段的清单看起来都做完了。'
          : 'この段階のチェックは一通りできています。';
    }
    const first = remaining[0]?.text ?? '';
    const more = remaining.length > 1 ? remaining.length - 1 : 0;
    if (locale === 'en') {
      return more > 0 ? `Still open: ${first} (+${more} more)` : `Still open: ${first}`;
    }
    if (locale === 'zh') {
      return more > 0 ? `还剩：${first}（另有${more}项）` : `还剩：${first}`;
    }
    return more > 0 ? `まだ：${first}（ほか${more}件）` : `まだ：${first}`;
  }, [activeStage, user, locale]);

  useEffect(() => {
    const prefs = loadReminderPrefs();
    const name = user.account?.name ? `${user.account.name}さん、` : '';
    void maybeLocalDailyReminder({
      prefs,
      title: ui.reminder.testTitle,
      body: uncheckedHint || `${name}今日の準備、終わっていますか？`,
    });
  }, [ui.reminder.testTitle, uncheckedHint, user.account?.id, user.account?.name]);

  const visibleCheckedCount = useMemo(() => {
    let done = 0;
    for (const stage of stages) {
      for (const item of stage.checklist) {
        if (user.isChecked(stage.id, item.id)) done += 1;
      }
    }
    return done;
  }, [stages, user]);

  const checklist: UseChecklistResult = useMemo(
    () => ({
      toggle: user.toggleChecked,
      isChecked: user.isChecked,
      checkedCount: visibleCheckedCount,
      totalCount,
      stageCheckedCount: (stageId: string) => {
        const stage = stages.find((s) => s.id === stageId);
        if (!stage) return 0;
        return stage.checklist.filter((item) => user.isChecked(stageId, item.id)).length;
      },
      stageProgress: (stageId: string, itemCount: number) => {
        if (itemCount === 0) return 0;
        const stage = stages.find((s) => s.id === stageId);
        if (!stage) return 0;
        const done = stage.checklist.filter((item) => user.isChecked(stageId, item.id)).length;
        return done / itemCount;
      },
      overallProgress: totalCount === 0 ? 0 : visibleCheckedCount / totalCount,
      resetStage: user.resetStage,
      resetAll: user.resetAllChecked,
      importChecked: user.importChecked,
    }),
    [user, totalCount, stages, visibleCheckedCount]
  );

  const hasProfile = Boolean(user.account?.dueDate || user.account?.birthDate);
  const showOnboarding = user.needsOnboarding;

  function selectStage(id: string) {
    setStageTouched(true);
    setActiveStageId(id);
  }

  async function handleRestoreCode(code: string) {
    const loaded = await loadBackupFromCloud(code);
    if (!loaded.ok) {
      return { ok: false, message: ui.onboarding.restoreFailed };
    }
    const ok = user.applyBackup(loaded.payload);
    if (ok) {
      setLocale(loaded.payload.account.locale);
      setStageTouched(false);
      return { ok: true, message: ui.onboarding.restoreOk };
    }
    return { ok: false, message: ui.onboarding.restoreFailed };
  }

  async function handleCloudSave() {
    const payload = user.exportActiveBackup();
    if (!payload) {
      return { ok: false as const, message: ui.settings.cloudSaveFailed };
    }
    const saved = await saveBackupToCloud(payload);
    if (!saved.ok) {
      return { ok: false as const, message: ui.settings.cloudSaveFailed };
    }
    return { ok: true as const, code: saved.code, message: ui.settings.cloudSaved.replace('{code}', saved.code) };
  }

  return (
    <div className={styles.app}>
      <LanguageSwitcher onLocaleChange={user.setLocaleForActive} />
      {user.account ? (
        <button
          type="button"
          className={styles.userBadge}
          onClick={() => setSettingsOpen(true)}
          aria-label={ui.settings.title}
        >
          {user.account.name}
        </button>
      ) : null}

      <Hero
        overallProgress={checklist.overallProgress}
        isDark={isDark}
        onToggleTheme={toggle}
        snapshotLabel={hasProfile ? user.snapshot.label : ''}
      />

      <div className={styles.toolbar}>
        <SearchBox stages={stages} checklist={checklist} onSelectStage={selectStage} />
      </div>

      {hasProfile && <WeekCard snapshot={user.snapshot} />}

      <TodayFocus
        stage={activeStage}
        checklist={checklist}
        snapshotLabel={user.snapshot.label}
        hasProfile={hasProfile}
        onOpenSettings={() => setSettingsOpen(true)}
      />

      <div className={styles.toolbarShare}>
        <SharePanel
          stages={stages}
          checklist={checklist}
          exportBackup={user.exportActiveBackup}
          onSyncFromBackup={user.applyFamilyShare}
          syncPrefs={familySyncPrefs}
          onSyncPrefsChange={setFamilySyncPrefs}
        />
      </div>

      <StageNav
        stages={stages}
        active={activeStageId}
        onSelect={selectStage}
        stageProgress={checklist.stageProgress}
      />

      <main className={styles.main}>
        <InstallAppCard />
        <ReminderCard
          accountName={user.account?.name ?? ''}
          uncheckedHint={uncheckedHint}
        />
        <StageView
          key={`${user.account?.id ?? 'none'}-${activeStage.id}`}
          stage={activeStage}
          checklist={checklist}
          checklistEditor={{
            hideItem: user.hideChecklistItem,
            removeCustomItem: user.removeCustomChecklistItem,
            renameItem: user.renameChecklistItem,
            addItem: user.addChecklistItem,
            restoreStageDefaults: user.restoreStageDefaults,
          }}
          shoppingEditor={{
            hideItem: user.hideShoppingItem,
            removeCustomItem: user.removeCustomShoppingItem,
            renameItem: user.renameShoppingItem,
            addItem: user.addShoppingItem,
          }}
        />
      </main>
      <Disclaimer />

      <Onboarding
        open={showOnboarding}
        suggestedDueDate={user.suggestedDueDate}
        onLocalePreview={(next) => {
          setLocale(next);
          user.setLocaleForActive(next);
        }}
        onComplete={(input) => {
          user.createOrUpdateFromOnboarding(input);
          setLocale(input.locale);
          setStageTouched(false);
        }}
        onRestoreCode={handleRestoreCode}
      />
      <ProfileSettings
        open={settingsOpen}
        account={user.account}
        accounts={user.accounts}
        suggestedDueDate={user.suggestedDueDate}
        onSaveDueDate={(value) => user.updateActiveDates({ dueDate: value })}
        onSaveBirthDate={(value) => user.updateActiveDates({ birthDate: value })}
        onClear={user.clearActiveDates}
        onRename={user.renameActive}
        onSwitch={(id) => {
          user.switchAccount(id);
          setStageTouched(false);
        }}
        onAdd={() => {
          user.addBlankAccount();
          setStageTouched(false);
        }}
        onDelete={user.deleteAccount}
        onLocaleChange={(next) => {
          setLocale(next);
          user.setLocaleForActive(next);
        }}
        onCloudSave={handleCloudSave}
        onCloudRestore={handleRestoreCode}
        onClose={() => setSettingsOpen(false)}
      />
    </div>
  );
}

export default App;
